import { useMemo, useState, useEffect, useRef } from "react";
import { r2Url } from "@/lib/r2";
import { ChevronLeft, ChevronRight } from "lucide-react";

type ProductGalleryProps = {
  // Accepts R2 keys (products/uuid/web/main.webp) or full URLs.
  images: string[];
  name: string;
};

export default function ProductGallery({ images, name }: ProductGalleryProps) {
  const LENS_SIZE = 120;
  const ZOOM_BACKGROUND_SIZE = '320%';

  // Normalize each image to a usable URL via r2Url helper.
  const resolvedImages = useMemo(
    () => (images ?? []).map((img) => r2Url(img)).filter(Boolean),
    [images]
  );

  const [activeImage, setActiveImage] = useState<string>(resolvedImages[0] ?? "");
  const [isZooming, setIsZooming] = useState(false);
  const [isOverNavButtons, setIsOverNavButtons] = useState(false);
  const [supportsHover, setSupportsHover] = useState(false);
  const [zoomPosition, setZoomPosition] = useState({ x: 0, y: 0 });
  const [lensPosition, setLensPosition] = useState({ x: 0, y: 0 });
  const imageRef = useRef<HTMLDivElement>(null);

  // Reset active image when image list changes (e.g. product/variant switch).
  useEffect(() => {
    setActiveImage(resolvedImages[0] ?? "");
  }, [resolvedImages]);

  useEffect(() => {
    if (typeof window === "undefined") return;

    const mediaQuery = window.matchMedia("(hover: hover) and (pointer: fine)");
    const updateHoverSupport = () => setSupportsHover(mediaQuery.matches);

    updateHoverSupport();

    if (typeof mediaQuery.addEventListener === "function") {
      mediaQuery.addEventListener("change", updateHoverSupport);
      return () => mediaQuery.removeEventListener("change", updateHoverSupport);
    }

    mediaQuery.addListener(updateHoverSupport);
    return () => mediaQuery.removeListener(updateHoverSupport);
  }, []);

  // Navigate to previous/next image
  const currentIndex = resolvedImages.indexOf(activeImage);

  const goToPrevious = () => {
    if (currentIndex > 0) {
      setActiveImage(resolvedImages[currentIndex - 1]);
    }
  };

  const goToNext = () => {
    if (currentIndex < resolvedImages.length - 1) {
      setActiveImage(resolvedImages[currentIndex + 1]);
    }
  };

  // Handle mouse move for zoom effect
  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!supportsHover || !imageRef.current) return;

    const rect = imageRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    // Smaller lens + larger zoom background creates a clearer magnified preview.
    const lensSize = LENS_SIZE;

    // Calculate lens position (centered on cursor)
    let lensX = x - lensSize / 2;
    let lensY = y - lensSize / 2;

    // Keep lens within bounds
    lensX = Math.max(0, Math.min(lensX, rect.width - lensSize));
    lensY = Math.max(0, Math.min(lensY, rect.height - lensSize));

    setLensPosition({ x: lensX, y: lensY });

    // Calculate zoom position for the zoomed window (percentage)
    const zoomX = (lensX + lensSize / 2) / rect.width * 100;
    const zoomY = (lensY + lensSize / 2) / rect.height * 100;

    setZoomPosition({ x: zoomX, y: zoomY });
  };

  const handleMouseEnter = () => {
    if (supportsHover && !isOverNavButtons) {
      setIsZooming(true);
    }
  };

  const handleMouseLeave = () => {
    setIsZooming(false);
  };

  if (!resolvedImages.length) {
    return (
      <div className="grid aspect-square place-items-center rounded-[18px] border border-[#e6e6e6] bg-white">
        <span className="text-sm font-semibold text-[#747982]">No images</span>
      </div>
    );
  }

  return (
    <div className="overflow-hidden rounded-[18px] border border-[#e6e6e6] bg-white p-4 shadow-sm lg:sticky lg:top-24">
      <div className="flex flex-col-reverse gap-3 sm:gap-4 lg:flex-row">
      {/* Thumbnails */}
      <div className="flex gap-2 overflow-x-auto scrollbar-hide sm:gap-3 lg:max-h-[640px] lg:flex-col lg:overflow-y-auto">
        {resolvedImages.map((img, idx) => (
          <button
            key={img}
            onClick={() => setActiveImage(img)}
            onMouseEnter={() => setActiveImage(img)}
            className={`relative h-14 w-14 flex-shrink-0 overflow-hidden rounded-[12px] border-2 bg-[#f7f7f7] transition-all sm:h-16 sm:w-16 lg:h-20 lg:w-20 ${
              activeImage === img
                ? 'border-[#f6a800] shadow-[0_6px_18px_rgba(246,168,0,0.24)]'
                : 'border-[#ececec] hover:border-[#d8d8d8]'
            }`}
          >
            <img src={img} alt={`${name} ${idx}`} className="h-full w-full object-contain p-1" />
          </button>
        ))}
      </div>

      {/* Main Image */}
      <div className="relative flex-1">
        <div
          ref={imageRef}
          className="relative aspect-[1/1.03] w-full overflow-hidden rounded-[16px] bg-[#f7f7f7] lg:aspect-auto lg:h-[72vh] lg:max-h-[760px]"
          onMouseMove={handleMouseMove}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          style={{ cursor: isZooming && !isOverNavButtons ? 'crosshair' : 'default' }}
        >
          <img
            src={activeImage}
            alt={name}
            className="h-full w-full object-contain object-center p-5 sm:p-7"
          />

          {/* Lens overlay - shows which area is being magnified */}
          {supportsHover && isZooming && !isOverNavButtons && (
            <div
              className="pointer-events-none absolute z-20 border-2 border-[#f6a800] bg-[rgba(246,168,0,0.08)]"
              style={{
                width: `${LENS_SIZE}px`,
                height: `${LENS_SIZE}px`,
                left: `${lensPosition.x}px`,
                top: `${lensPosition.y}px`,
              }}
            />
          )}

          {/* Navigation arrows only when there are multiple images */}
          {resolvedImages.length > 1 && (
            <div
              className="absolute bottom-3 right-3 sm:bottom-4 sm:right-4 flex gap-2 z-10"
              onMouseEnter={() => {
                setIsOverNavButtons(true);
                setIsZooming(false);
              }}
              onMouseLeave={() => {
                setIsOverNavButtons(false);
                if (supportsHover) setIsZooming(true);
              }}
            >
              {/* Previous Button */}
              <button
                onClick={goToPrevious}
                disabled={currentIndex === 0}
              className={`rounded-full border border-[#e8e8e8] bg-white/95 p-2.5 shadow-lg backdrop-blur-sm transition-all hover:scale-105 sm:p-3 ${
                  currentIndex === 0
                    ? 'opacity-50 cursor-not-allowed'
                    : 'hover:bg-white'
                }`}
                aria-label="Previous image"
              >
                <ChevronLeft size={20} className="text-[#111318]" />
              </button>

              {/* Next Button */}
              <button
                onClick={goToNext}
                disabled={currentIndex === resolvedImages.length - 1}
              className={`rounded-full border border-[#e8e8e8] bg-white/95 p-2.5 shadow-lg backdrop-blur-sm transition-all hover:scale-105 sm:p-3 ${
                  currentIndex === resolvedImages.length - 1
                    ? 'opacity-50 cursor-not-allowed'
                    : 'hover:bg-white'
                }`}
                aria-label="Next image"
              >
                <ChevronRight size={20} className="text-[#111318]" />
              </button>
            </div>
          )}
        </div>

        {/* Zoom Window - absolute positioned, appears on the right when hovering */}
        {supportsHover && isZooming && !isOverNavButtons && imageRef.current && (
          <div
            className="fixed z-50 overflow-hidden rounded-[18px] border border-[#e6e6e6] bg-white shadow-2xl animate-in fade-in zoom-in-95 duration-200"
            style={{
              width: '500px',
              height: '500px',
              top: `${imageRef.current.getBoundingClientRect().top}px`,
              left: `${imageRef.current.getBoundingClientRect().right + 20}px`,
            }}
          >
            <div
              className="w-full h-full"
              style={{
                backgroundImage: `url("${activeImage}")`,
                backgroundSize: ZOOM_BACKGROUND_SIZE,
                backgroundPosition: `${zoomPosition.x}% ${zoomPosition.y}%`,
                backgroundRepeat: 'no-repeat',
              }}
            />
          </div>
        )}
      </div>
      </div>
    </div>
  );
}
